import { IconStation, TooltipType } from "@/types";
import { useCallback, useMemo, useState } from "react";

import { IconLayer } from "@deck.gl/layers";
import { LocationModalState } from "@/types/address.type";
import { PermitLocation } from "@/types/decree.type";
import { PickingInfo } from "deck.gl";
import { RestrictionModeType } from "../components/map-tool-box/restriction-tool-box-entry.const";
import { createMarkerLayer } from "@/helper/map-utils";
import { useQueryPermitLocation } from "@/modules/restriction/hooks/use-query-permit-location";

type PermitLocationFeatureProps = {
  appMode: RestrictionModeType;
};
export const usePermitLocationFeature = ({ appMode }: PermitLocationFeatureProps) => {
  const [permitLocationModalState, setPermitLocationModalState] = useState<keyof typeof LocationModalState>(
    LocationModalState.DEACTIVATE,
  );
  const [permitLocationDetail, setPermitLocationDetail] = useState<PermitLocation | null>(null);
  const [hoverPermitLocationInfo, setHoverPermitLocationInfo] = useState<TooltipType<PermitLocation> | null>(null);

  const { permitLocationData, createPermitLocation, updatePermitLocation, removePermitLocation } =
    useQueryPermitLocation();
  const handleHoverPermitLocation = useCallback(
    (info: PickingInfo) => {
      if (info.object && appMode === RestrictionModeType.View) {
        setHoverPermitLocationInfo({
          x: info.x,
          y: info.y,
          properties: info.object.properties,
        });
      } else {
        setHoverPermitLocationInfo(null);
      }
    },
    [appMode],
  );
  const permitLocationLayer = new IconLayer({
    id: "permit-location-icon-layer",
    data: permitLocationData,
    pickable: [RestrictionModeType.EditPermitLocation, RestrictionModeType.View].includes(appMode),
    iconAtlas: "images/map_pointer.png",
    iconMapping: {
      marker: { x: 0, y: 0, width: 34, height: 50 },
    },
    getIcon: () => "marker",
    sizeScale: 20,
    getPosition: (d: IconStation) => d.coordinates,
    getSize: () => 2,
    onClick: (info) => {
      if (appMode !== RestrictionModeType.EditPermitLocation) {
        return;
      }
      const { coordinates, ...rest } = info.object;
      setPermitLocationDetail({
        ...rest,
        position: { type: "Point", coordinates },
      });
    },
    onHover: (info) => {
      if (appMode !== RestrictionModeType.View) {
        return;
      }
      handleHoverPermitLocation(info);
    },
    updateTriggers: {
      onHover: [appMode],
      onClick: [appMode],
    },
  });

  const markerLayer = useMemo(() => {
    if (!permitLocationDetail?.position.coordinates?.length) {
      return null;
    }
    return createMarkerLayer({
      coordinates: [permitLocationDetail.position.coordinates[0], permitLocationDetail.position.coordinates[1]],
    });
  }, [permitLocationDetail]);
  const permitLocationMapLayers = [permitLocationLayer, markerLayer];

  const handleClose = () => {
    setPermitLocationModalState(LocationModalState.DEACTIVATE);
    setPermitLocationDetail(null);
  };

  const savePermitLocation = async (input: PermitLocation) => {
    const permitLocationInput = {
      decreeId: input.selectedDecrees[0].value,
      location: {
        address: input.address,
        position: input.position,
      },
    };
    if (input.id) {
      await updatePermitLocation(input);
    } else {
      await createPermitLocation({ variables: { ...permitLocationInput } });
    }
    handleClose();
  };

  const handleRemovePermitLocation = async () => {
    if (permitLocationDetail) {
      await removePermitLocation({
        variables: { locationId: permitLocationDetail.id, decreeId: permitLocationDetail.selectedDecrees[0].value },
      });
      setPermitLocationDetail(null);
    }
  };

  return {
    permitLocationMapLayers,
    savePermitLocation,
    handleRemovePermitLocation,
    permitLocationModalState,
    hoverPermitLocationInfo,
    permitLocationDetail,
    setPermitLocationDetail,
    setPermitLocationModalState,
    handleClose,
  };
};
